import * as THREE from 'three';
import { C } from '../core/palette.js';
import { box, cyl, mat, clamp01, angleLerp } from '../core/meshkit.js';
import { findCosmetic } from './cosmetics.js';
import { getEquipped } from '../core/save.js';

// Bread Boi: the "Toast" silhouette (wide slice-of-toast with a gentle two-bump
// top + shallow center dip, golden crust, cream crumb), the eyed grin face (with
// blink), and stick limbs with elbows/knees. Reusable across every scene. Root
// sits on the floor at y=0; call update(dt) each frame and walkTo(target) to move.

// The "Toast" silhouette from the toast-world prototype: a wide slice-of-toast
// with a gentle two-bump top and a shallow center dip (was: the taller
// two-lobe loaf). Kept as the game's canonical character shape.
export function breadShape(s){
  const sh = new THREE.Shape();
  sh.moveTo(-.72*s, -1.15*s);
  sh.quadraticCurveTo(-1.02*s, -1.15*s, -1.02*s, -.85*s);
  sh.lineTo(-1.06*s, .45*s);
  sh.bezierCurveTo(-1.10*s, 1.28*s, -.30*s, 1.34*s, -.03*s, .92*s);
  sh.bezierCurveTo(.16*s, 1.36*s, 1.02*s, 1.30*s, 1.05*s, .45*s);
  sh.lineTo(1.02*s, -.85*s);
  sh.quadraticCurveTo(1.02*s, -1.15*s, .72*s, -1.15*s);
  sh.closePath();
  return sh;
}

const HIP_Y = 0.8;   // short-king proportions

function segment(len, r, color, tipR){
  const g = new THREE.Group();
  const geo = new THREE.CylinderGeometry(r, r, len, 8);
  geo.translate(0, -len/2, 0);
  const m = new THREE.Mesh(geo, mat(color));
  m.castShadow = true;
  g.add(m);
  const end = new THREE.Group();
  end.position.y = -len;
  g.add(end);
  if (tipR){
    const tip = new THREE.Mesh(new THREE.SphereGeometry(tipR, 10, 10), mat(color));
    tip.castShadow = true;
    end.add(tip);
  }
  return { root: g, end };
}

export class BreadBoi {
  constructor(){
    this.root = new THREE.Group();
    this.root.scale.setScalar(0.65);   // match the townsfolk scale

    this.body = new THREE.Group();
    this.body.position.y = HIP_Y + 1.3;
    this.root.add(this.body);

    // golden toast crust + cream crumb (the toast-world look)
    this.crustMat = new THREE.MeshLambertMaterial({ color: C.crust });
    this.crumbMat = new THREE.MeshLambertMaterial({ color: C.cream });

    const crust = new THREE.Mesh(
      new THREE.ExtrudeGeometry(breadShape(1), { depth:.5, bevelEnabled:true, bevelSize:.06, bevelThickness:.06, bevelSegments:2 }),
      this.crustMat
    );
    crust.position.z = -.28; crust.castShadow = true;
    this.body.add(crust);

    const inner = new THREE.Mesh(
      new THREE.ExtrudeGeometry(breadShape(.86), { depth:.56, bevelEnabled:false }),
      this.crumbMat
    );
    inner.position.z = -.27;
    this.body.add(inner);

    this._buildFace();
    this._buildLimbs();

    // wardrobe anchors
    this.anchors = {
      hat:   new THREE.Group(),
      tie:   new THREE.Group(),
      shirt: new THREE.Group()
    };
    this.anchors.hat.position.set(0, 1.38, 0);
    this.anchors.tie.position.set(0, -.62, .36);
    this.body.add(this.anchors.hat, this.anchors.tie, this.anchors.shirt);
    this.applyWardrobe();               // wear whatever's equipped in the save

    // movement state (flat walk speed — cars are for the Speedway now)
    this.speed = 2.6;

    // personal lantern (used in dark scenes when the player has the ghost-lamp)
    this.lantern = new THREE.PointLight(0xFFCC66, 0, 14, 1.6);
    this.lantern.position.set(0, 1.6, 0.35);
    this.root.add(this.lantern);

    this.walkPhase = 0;
    this.target = null;
    this.onArrive = null;
    this.facing = 0;

    // pose animation state for workouts / sleep / meditate
    this.pose = null;          // 'lift' | 'run' | 'sleep' | 'meditate'
    this.poseT = 0;            // seconds remaining; Infinity = indefinite
    this.posePhase = 0;
  }

  _buildFace(){
    const cv = document.createElement('canvas');
    cv.width = cv.height = 256;
    this._fg = cv.getContext('2d');
    this._faceTex = new THREE.CanvasTexture(cv);
    this.expr = 'happy';
    this.blinkT = 0;                      // >0 while mid-blink
    this._blinkTimer = 2 + Math.random() * 3;
    this.drawFace('happy');
    const plane = new THREE.Mesh(
      new THREE.PlaneGeometry(1.9, 1.9),
      new THREE.MeshBasicMaterial({ map: this._faceTex, transparent: true })
    );
    plane.position.set(0, -.02, .31);
    this.body.add(plane);
  }

  // Toast's face: two curved eyes + an open grin, with a few expressions.
  // 'happy' and 'grin' both read as the big open smile.
  drawFace(expr){
    this.expr = expr;
    const g = this._fg;
    g.clearRect(0, 0, 256, 256);
    g.strokeStyle = '#3A2A1C'; g.lineWidth = 9; g.lineCap = 'round';
    const squeeze = this.blinkT > 0 ? .25 : 1;
    const eyeArc = (cx) => {
      g.beginPath();
      g.ellipse(cx, 104, 22, 14 * squeeze, 0, Math.PI * 1.15, Math.PI * 1.85);
      g.stroke();
    };
    const eyeShut = (cx, flip) => {
      g.beginPath();
      g.moveTo(cx - 16, 96); g.lineTo(cx + (flip ? -2 : 2), 106); g.lineTo(cx - 16, 116);
      g.moveTo(cx + 16, 96); g.lineTo(cx - (flip ? -2 : 2), 106); g.lineTo(cx + 16, 116);
      g.stroke();
    };

    if (expr === 'effort'){
      eyeShut(86); eyeShut(170);
      g.fillStyle = '#3A2A1C';
      g.beginPath(); g.ellipse(128, 168, 14, 17, 0, 0, Math.PI * 2); g.fill();
    } else if (expr === 'o'){
      eyeArc(86); eyeArc(170);
      g.fillStyle = '#3A2A1C';
      g.beginPath(); g.arc(128, 170, 20, 0, Math.PI * 2); g.fill();
      g.fillStyle = '#E6537B';
      g.beginPath(); g.arc(128, 178, 10, 0, Math.PI); g.fill();
    } else if (expr === 'yum'){
      eyeArc(86); eyeArc(170);
      g.fillStyle = '#3A2A1C';
      g.beginPath(); g.arc(128, 162, 26, 0, Math.PI); g.closePath(); g.fill();
      g.fillStyle = '#E6537B';
      g.beginPath(); g.ellipse(128, 186, 13, 10, 0, Math.PI, 0); g.fill();
    } else { // happy / grin — big open smile
      eyeArc(86); eyeArc(170);
      g.fillStyle = '#3A2A1C';
      g.beginPath();
      g.moveTo(100, 148);
      g.quadraticCurveTo(128, 140, 156, 148);
      g.quadraticCurveTo(162, 190, 128, 196);
      g.quadraticCurveTo(94, 190, 100, 148);
      g.fill();
      g.fillStyle = '#E6537B';
      g.beginPath(); g.ellipse(128, 188, 15, 9, 0, Math.PI, 0); g.fill();
    }
    this._faceTex.needsUpdate = true;
  }

  // Occasional blink — redraws the current expression with squeezed eyes.
  _blink(dt){
    if (this.blinkT > 0){
      this.blinkT -= dt;
      if (this.blinkT <= 0){ this.blinkT = 0; this.drawFace(this.expr); }
    } else {
      this._blinkTimer -= dt;
      if (this._blinkTimer <= 0){
        this.blinkT = 0.12; this._blinkTimer = 2.5 + Math.random() * 3;
        this.drawFace(this.expr);
      }
    }
  }

  _buildLimbs(){
    const ARM = { shoulderZ: 1.9, elbowZ: -.25 };
    this.ARM = ARM;
    const makeArm = (side) => {
      const upper = segment(.42, .042, C.limb, 0);
      const fore  = segment(.36, .04,  C.limb, .055);
      upper.end.add(fore.root);
      upper.root.position.set(side * 1.0, .05, .04);
      upper.root.rotation.z = side * ARM.shoulderZ;
      fore.root.rotation.z  = side * ARM.elbowZ;
      this.body.add(upper.root);
      return { shoulder: upper.root, elbow: fore.root, side };
    };
    this.armL = makeArm(-1);
    this.armR = makeArm(1);

    const makeLeg = (x) => {
      const thigh = segment(.44, .046, C.limb, 0);
      const shin  = segment(.38, .044, C.limb, .07);
      thigh.end.add(shin.root);
      thigh.root.position.set(x, HIP_Y, 0);
      this.root.add(thigh.root);
      return { hip: thigh.root, knee: shin.root };
    };
    this.legL = makeLeg(-.28);
    this.legR = makeLeg(.28);
  }

  resetLimbs(){
    [this.armL, this.armR].forEach(a => {
      a.shoulder.rotation.set(0, 0, a.side * this.ARM.shoulderZ);
      a.elbow.rotation.set(0, 0, a.side * this.ARM.elbowZ);
    });
    [this.legL, this.legR].forEach(l => {
      l.hip.rotation.set(0, 0, 0);
      l.knee.rotation.set(0, 0, 0);
    });
    this.body.rotation.set(0, 0, 0);
    this.body.position.y = HIP_Y + 1.3;
  }

  // Start a reusable pose animation. Stops walking and resets limbs first.
  startPose(name, duration = Infinity){
    this.stop();
    this.resetLimbs();
    this.pose = name;
    this.poseT = duration;
    this.poseDuration = duration;
    this.posePhase = 0;
  }

  stopPose(){
    this.pose = null;
    this.poseT = 0;
    this.posePhase = 0;
    this.resetLimbs();
  }

  _updatePose(dt){
    this.posePhase += dt;
    const p = this.posePhase;
    if (this.pose === 'lift'){
      const curl = (Math.sin(p * 5) + 1) * 0.5; // 0..1
      this.armL.shoulder.rotation.x = -1.4 + curl * 0.9;
      this.armR.shoulder.rotation.x = -1.4 + curl * 0.9;
      this.armL.elbow.rotation.x = -curl * 1.6;
      this.armR.elbow.rotation.x = -curl * 1.6;
      this.body.position.y = (HIP_Y + 1.3) - curl * 0.08;
    } else if (this.pose === 'run'){
      this.posePhase += dt * 12;
      const r = this.posePhase;
      this.root.position.y = Math.abs(Math.sin(r)) * 0.18;
      this.legL.hip.rotation.x = Math.sin(r) * 1.0;
      this.legR.hip.rotation.x = Math.sin(r + Math.PI) * 1.0;
      this.legL.knee.rotation.x = Math.max(0, Math.sin(r + 1.2)) * 0.9;
      this.legR.knee.rotation.x = Math.max(0, Math.sin(r + Math.PI + 1.2)) * 0.9;
      this.armL.shoulder.rotation.x = Math.sin(r + Math.PI) * 0.7;
      this.armR.shoulder.rotation.x = Math.sin(r) * 0.7;
      this.body.rotation.z = Math.sin(r * 0.5) * 0.08;
    } else if (this.pose === 'sleep'){
      this.body.rotation.x = 1.05;
      this.body.position.y = (HIP_Y + 1.3) - 0.55;
      this.armL.shoulder.rotation.x = -0.6; this.armL.elbow.rotation.x = -0.4;
      this.armR.shoulder.rotation.x = -0.6; this.armR.elbow.rotation.x = -0.4;
      this.legL.hip.rotation.x = -0.7; this.legL.knee.rotation.x = 1.0;
      this.legR.hip.rotation.x = -0.5; this.legR.knee.rotation.x = 1.0;
    } else if (this.pose === 'meditate'){
      const float = Math.sin(p * 2) * 0.04;
      this.root.position.y = 0.12 + float;
      this.legL.hip.rotation.x = -1.1; this.legL.hip.rotation.z = -0.6;
      this.legL.knee.rotation.x = 1.4;
      this.legR.hip.rotation.x = -1.1; this.legR.hip.rotation.z = 0.6;
      this.legR.knee.rotation.x = 1.4;
      this.armL.shoulder.rotation.x = -0.3; this.armL.shoulder.rotation.z = 0.4;
      this.armR.shoulder.rotation.x = -0.3; this.armR.shoulder.rotation.z = -0.4;
      this.armL.elbow.rotation.x = -0.5; this.armR.elbow.rotation.x = -0.5;
      this.body.rotation.y = Math.sin(p * 0.8) * 0.08;
    } else if (this.pose === 'strike'){
      const dur = this.poseDuration === Infinity ? 0.4 : this.poseDuration;
      const t = Math.min(1, p / Math.max(0.01, dur));
      const wind = Math.min(1, t * 2);          // 0..1 over first half
      const swing = Math.max(0, (t - 0.5) * 2); // 0..1 over second half
      this.armR.shoulder.rotation.x = -0.8 - wind * 1.8 + swing * 2.4;
      this.armR.elbow.rotation.x = -wind * 1.2 + swing * 0.4;
      this.armL.shoulder.rotation.x = 0.4 + swing * 0.6;
      this.body.rotation.y = -wind * 0.5 + swing * 0.8;
      this.body.rotation.z = swing * 0.2;
      this.root.position.y = Math.sin(t * Math.PI) * 0.1;
    }
  }

  // Rebuild the cosmetic meshes on the wardrobe anchors from the current save.
  // Cheap enough to call live whenever the player equips/unequips at the shop.
  applyWardrobe(){
    for (const slot of Object.keys(this.anchors)){
      const anchor = this.anchors[slot];
      for (let i = anchor.children.length - 1; i >= 0; i--) anchor.remove(anchor.children[i]);
      const item = findCosmetic(getEquipped(slot));
      if (item) anchor.add(item.build());
    }
  }

  setLantern(intensity = 0){ this.lantern.intensity = intensity; }

  get position(){ return this.root.position; }

  walkTo(target, onArrive){
    this.target = target.clone();
    this.onArrive = onArrive || null;
  }
  stop(){ this.target = null; this.onArrive = null; }

  update(dt){
    this._blink(dt);
    if (this.pose){
      this._updatePose(dt);
      if (this.poseT !== Infinity){
        this.poseT -= dt;
        if (this.poseT <= 0){ this.stopPose(); return; }
      }
      return;
    }
    if (this.target){
      const pos = this.root.position;
      const dir = new THREE.Vector3().subVectors(this.target, pos); dir.y = 0;
      const dist = dir.length();
      if (dist < 0.1){
        pos.copy(this.target); pos.y = 0;
        this.target = null;
        this.resetLimbs();
        const cb = this.onArrive; this.onArrive = null;
        if (cb) cb();
      } else {
        dir.normalize();
        this.facing = Math.atan2(dir.x, dir.z);
        this.root.rotation.y = angleLerp(this.root.rotation.y, this.facing, Math.min(1, dt*8));
        pos.addScaledVector(dir, this.speed * dt);
        this._animateWalk(dt);
      }
    } else {
      // gentle idle bob
      this.root.position.y = Math.abs(Math.sin(performance.now() * 0.0024)) * 0.02;
    }
  }

  _animateWalk(dt){
    this.walkPhase += dt * 9;
    this.root.position.y = Math.abs(Math.sin(this.walkPhase)) * 0.14;
    this.legL.hip.rotation.x = Math.sin(this.walkPhase) * 0.7;
    this.legR.hip.rotation.x = Math.sin(this.walkPhase + Math.PI) * 0.7;
    this.legL.knee.rotation.x = Math.max(0, Math.sin(this.walkPhase + 1.2)) * 0.6;
    this.legR.knee.rotation.x = Math.max(0, Math.sin(this.walkPhase + Math.PI + 1.2)) * 0.6;
    this.armL.shoulder.rotation.x = Math.sin(this.walkPhase + Math.PI) * 0.45;
    this.armR.shoulder.rotation.x = Math.sin(this.walkPhase) * 0.45;
    this.body.rotation.z = Math.sin(this.walkPhase * 0.5) * 0.05;
  }
}
